import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Header } from "../components/Header";
import { TeamBadge } from "../components/TeamBadge";
import { useSport } from "../context/SportContext";
import { ArrowsLeftRight, ShieldCheck } from "@phosphor-icons/react";

const FormDots = ({ form }) => (
  <div className="flex gap-0.5">
    {form?.map((r, i) => (
      <span key={i} className="w-1.5 h-1.5 rounded-full"
        style={{ background: r === "W" ? "#00E676" : r === "D" ? "#FB8C00" : "#E53935" }} />
    ))}
  </div>
);

export default function Stats() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sport, meta } = useSport();
  const [params] = useSearchParams();
  const [comps, setComps] = useState([]);
  const [comp, setComp] = useState(params.get("comp") || "ligue1");
  const [venue, setVenue] = useState("global");
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("classement");
  const [metric, setMetric] = useState("goals");
  const [scorers, setScorers] = useState([]);
  const [tour, setTour] = useState("ATP");
  const [ranks, setRanks] = useState([]);
  const [conf, setConf] = useState("east");
  const [nba, setNba] = useState({ east: [], west: [], season: null });

  useEffect(() => { if (sport === "foot") api.get("/competitions").then((r) => setComps(r.data)).catch(() => {}); }, [sport]);
  useEffect(() => { if (sport === "foot") api.get(`/competitions/${comp}/standings?venue=${venue}`).then((r) => setRows(r.data)).catch(() => {}); }, [comp, venue, sport]);
  useEffect(() => { if (sport === "foot") api.get(`/competitions/${comp}/scorers?metric=${metric}`).then((r) => setScorers(r.data)).catch(() => {}); }, [comp, metric, sport]);
  useEffect(() => { if (sport === "tennis") api.get(`/tennis/rankings?tour=${tour}&limit=100`).then((r) => setRanks(r.data.players || [])).catch(() => {}); }, [sport, tour]);
  useEffect(() => { if (sport === "basket") api.get(`/basket/standings`).then((r) => setNba(r.data)).catch(() => {}); }, [sport]);

  if (sport === "tennis") {
    return (
      <>
        <Header showSearch={false} />
        <div className="px-5 pt-5" data-testid="tennis-rankings">
          <div className="flex items-center justify-between mb-4">
            <h1 className="font-display text-2xl font-bold tracking-tight">{t("stats.rankings_title")}</h1>
            <button data-testid="tennis-compare-cta" onClick={() => navigate("/tennis/compare")}
              className="flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-lg"
              style={{ background: "rgba(212,175,55,0.14)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.35)" }}>
              <ArrowsLeftRight size={13} weight="bold" /> {t("compare.cta", "Comparer")}
            </button>
          </div>
          <div className="flex gap-1 p-1 rounded-lg bg-white/5 mb-4">
            {["ATP", "WTA"].map((k) => (
              <button key={k} data-testid={`tour-${k}`} onClick={() => setTour(k)}
                className={`flex-1 py-2 rounded-md text-sm font-bold transition-colors ${tour === k ? "bg-gold text-black" : "text-white/60"}`}>
                {k}
              </button>
            ))}
          </div>
          <div className="hm-card overflow-hidden" data-testid="rankings-table">
            <div className="grid grid-cols-[32px_1fr_64px] gap-2 px-3 py-2 text-[10px] font-bold uppercase text-white/40 border-b border-white/5">
              <span>#</span><span>{t("stats.col_player")}</span><span className="text-right">{t("stats.points")}</span>
            </div>
            {ranks.map((p) => (
              <button key={`${p.rank}-${p.name}`} data-testid={`rank-row-${p.rank}`}
                onClick={() => navigate(`/tennis/player/${encodeURIComponent(p.name)}`)}
                className="w-full grid grid-cols-[32px_1fr_64px] gap-2 px-3 py-2.5 items-center border-b border-white/5 last:border-0 hover:bg-white/5 text-left">
                <span className={`text-sm font-numeral font-bold ${p.rank <= 3 ? "text-gold" : "text-white/50"}`}>{p.rank}</span>
                <span className="flex items-center gap-2 min-w-0">
                  {p.flag ? <img src={p.flag} alt="" className="w-6 h-4 object-cover rounded-sm shrink-0" />
                    : <span className="w-6 h-4 rounded-sm bg-white/10 shrink-0" />}
                  <span className="text-sm font-semibold truncate">{p.name}</span>
                </span>
                <span className="text-right font-numeral font-bold text-gold text-sm">{p.points?.toLocaleString?.() || p.points}</span>
              </button>
            ))}
            {ranks.length === 0 && <p className="text-center text-[12px] text-white/35 py-8" data-testid="rankings-empty">{t("stats.coming_desc", { sport: "tennis" })}</p>}
          </div>
        </div>
      </>
    );
  }

  if (sport === "basket") {
    const list = conf === "east" ? nba.east : nba.west;
    return (
      <>
        <Header showSearch={false} />
        <div className="px-5 pt-5" data-testid="nba-standings">
          <div className="flex items-center justify-between mb-4">
            <h1 className="font-display text-2xl font-bold tracking-tight">{t("stats.standings_title")}</h1>
            <button data-testid="nba-compare-cta" onClick={() => navigate("/basket/compare")}
              className="flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-lg"
              style={{ background: "rgba(255,122,24,0.14)", color: "#FF7A18", border: "1px solid rgba(255,122,24,0.35)" }}>
              <ArrowsLeftRight size={13} weight="bold" /> {t("compare.cta", "Comparer")}
            </button>
          </div>
          <div className="flex gap-1 p-1 rounded-lg bg-white/5 mb-4">
            {[["east", t("stats.conf_east")], ["west", t("stats.conf_west")]].map(([k, l]) => (
              <button key={k} data-testid={`conf-${k}`} onClick={() => setConf(k)}
                className={`flex-1 py-2 rounded-md text-sm font-bold transition-colors ${conf === k ? "text-black" : "text-white/60"}`}
                style={conf === k ? { background: meta.color } : {}}>
                {l}
              </button>
            ))}
          </div>
          <div className="hm-card overflow-hidden" data-testid="standings-nba-table">
            <div className="grid grid-cols-[28px_1fr_52px_44px] gap-2 px-3 py-2 text-[10px] font-bold uppercase text-white/40 border-b border-white/5">
              <span>#</span><span>{t("stats.col_team")}</span><span className="text-center">V-D</span><span className="text-right">%</span>
            </div>
            {list.map((tm) => (
              <button key={tm.team} data-testid={`nba-row-${tm.rank}`}
                onClick={() => tm.id && navigate(`/basket/team/${tm.id}`)}
                className="w-full grid grid-cols-[28px_1fr_52px_44px] gap-2 px-3 py-2.5 items-center border-b border-white/5 last:border-0 hover:bg-white/5 text-left">
                <span className={`text-sm font-numeral font-bold ${tm.rank <= 6 ? "text-perf-excellent" : tm.rank <= 10 ? "text-white/70" : "text-white/40"}`}>{tm.rank}</span>
                <span className="flex items-center gap-2 min-w-0">
                  {tm.logo && <img src={tm.logo} alt="" className="w-6 h-6 object-contain shrink-0" />}
                  <span className="text-sm font-semibold truncate">{tm.team}</span>
                </span>
                <span className="text-center text-sm font-numeral text-white/70">{tm.wins}-{tm.losses}</span>
                <span className="text-right font-numeral font-bold text-sm" style={{ color: meta.color }}>{(tm.pct * 100).toFixed(0)}</span>
              </button>
            ))}
            {list.length === 0 && <p className="text-center text-[12px] text-white/35 py-8" data-testid="standings-empty">{t("stats.coming_desc", { sport: "basket" })}</p>}
          </div>
        </div>
      </>
    );
  }

  const activeComps = comps.filter((c) => c.active);

  return (
    <>
      <Header showSearch={false} />
      <div className="px-5 pt-5">
        <div className="flex items-center justify-between mb-1">
          <h1 className="font-display text-2xl font-bold tracking-tight">{t("stats.title")}</h1>
          <button data-testid="stats-compare-btn" onClick={() => navigate("/compare")}
            className="h-9 px-3 rounded-full glass flex items-center gap-1.5 text-xs font-bold text-gold">
            <ArrowsLeftRight size={15} weight="bold" /> {t("stats.compare")}
          </button>
        </div>
        <div className="flex items-center gap-1.5 mb-4" data-testid="official-data-badge">
          <ShieldCheck size={13} weight="fill" className="text-perf-excellent shrink-0" />
          <span className="text-[11px] text-white/50 font-medium">{t("stats.official_badge")}</span>
        </div>

        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {comps.map((c) => (
            <button
              key={c.comp_id}
              data-testid={`comp-${c.comp_id}`}
              disabled={!c.active}
              onClick={() => c.active && setComp(c.comp_id)}
              className={`px-3.5 py-2 rounded-full text-xs font-bold whitespace-nowrap border transition-colors ${
                comp === c.comp_id ? "bg-gold text-black border-gold"
                : c.active ? "border-white/15 text-white/70" : "border-white/5 text-white/25 cursor-not-allowed"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="flex gap-1 p-1 mt-5 rounded-lg bg-white/5">
          {[["classement", t("stats.tab_standings")], ["buteurs", t("stats.tab_scorers")]].map(([k, l]) => (
            <button key={k} data-testid={`stats-tab-${k}`} onClick={() => setTab(k)}
              className={`flex-1 py-2 rounded-md text-sm font-bold transition-colors ${tab === k ? "bg-gold text-black" : "text-white/60"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "classement" ? (
          <>
            <div className="flex gap-1.5 mt-4">
              {[["global", t("stats.venue_global")], ["home", t("stats.venue_home")], ["away", t("stats.venue_away")]].map(([k, l]) => (
                <button key={k} data-testid={`venue-${k}`} onClick={() => setVenue(k)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-bold border ${venue === k ? "bg-white/10 border-gold/40 text-gold" : "border-white/10 text-white/50"}`}>
                  {l}
                </button>
              ))}
            </div>
            <div className="hm-card mt-4 overflow-hidden" data-testid="standings-table">
              <div className="grid grid-cols-[24px_1fr_28px_28px_36px_32px] gap-2 px-3 py-2 text-[10px] font-bold uppercase text-white/40 border-b border-white/5">
                <span>#</span><span>{t("stats.col_team")}</span><span className="text-center">{t("stats.col_played")}</span>
                <span className="text-center">{t("stats.col_gd")}</span><span className="text-center">{t("stats.col_form")}</span><span className="text-right">{t("stats.col_pts")}</span>
              </div>
              {rows.map((r) => (
                <button key={r.team_id} data-testid={`standing-${r.team_id}`} onClick={() => navigate(`/club/${r.team_id}`)}
                  className="w-full grid grid-cols-[24px_1fr_28px_28px_36px_32px] gap-2 px-3 py-2.5 items-center hover:bg-white/5 border-b border-white/5 last:border-0">
                  <span className={`text-sm font-numeral font-bold ${r.rank <= 3 ? "text-gold" : "text-white/50"}`}>{r.rank}</span>
                  <span className="flex items-center gap-2 min-w-0">
                    <TeamBadge team={r} size={26} />
                    <span className="text-sm font-semibold truncate">{r.name}</span>
                  </span>
                  <span className="text-center text-sm text-white/60">{r.played}</span>
                  <span className="text-center text-sm text-white/60">{r.gd > 0 ? `+${r.gd}` : r.gd}</span>
                  <span className="flex justify-center"><FormDots form={r.form} /></span>
                  <span className="text-right font-numeral font-bold text-gold">{r.pts}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="flex gap-1.5 mt-4">
              {[["goals", t("stats.metric_goals")], ["assists", t("stats.metric_assists")]].map(([k, l]) => (
                <button key={k} data-testid={`metric-${k}`} onClick={() => setMetric(k)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-bold border ${metric === k ? "bg-white/10 border-gold/40 text-gold" : "border-white/10 text-white/50"}`}>
                  {l}
                </button>
              ))}
            </div>
            <div className="hm-card mt-4 overflow-hidden" data-testid="scorers-table">
              {scorers.map((p, i) => (
                <button key={p.player_id} data-testid={`scorer-${p.player_id}`} onClick={() => navigate(`/player/${p.player_id}${p.team_id ? `?team=${p.team_id}` : ""}`)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 border-b border-white/5 last:border-0">
                  <span className={`w-5 text-sm font-numeral font-bold ${i < 3 ? "text-gold" : "text-white/40"}`}>{i + 1}</span>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold">{p.name}</p>
                    <p className="text-[11px] text-white/40">{p.club} · {p.position}</p>
                  </div>
                  <span className="font-numeral font-black text-xl text-gold">{p.stats[metric]}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
